import { Dispatch, forwardRef, SetStateAction, useContext } from 'react';
import { Button } from '../../ui/button';
import { cn } from '@/lib/utils';
import { ConfigContext } from '@/config/config-context';
type ChatWindowEndProps = {
	chatWindowEnd: boolean;
	setChatWindowEnd: Dispatch<SetStateAction<boolean>>;
	handleEndConversation: () => void;
	setExpanded: Dispatch<SetStateAction<boolean>>;
};

/**
 * ChatWindowEnd component renders a modal to confirm ending the chat conversation.
 *
 * This component displays a modal overlay with options to either cancel or end the chat.
 * When the "Cancel" button is clicked, it closes the modal. When the "End Chat" button is
 * clicked, it clears the conversation and closes the modal.
 *
 * @param {object} props - The properties object.
 * @param {boolean} props.chatWindowEnd - A state indicating if the chat window end modal is displayed.
 * @param {Dispatch<SetStateAction<boolean>>} props.setChatWindowEnd - A function to update the chat window end state.
 * @param {Dispatch<SetStateAction<BubbleComponentType[]>>} props.setConversation - A function to update the conversation state.
 * @param {Dispatch<SetStateAction<boolean>>} props.setExpanded - A function to minimize the chat window.
 * @returns {JSX.Element} The ChatWindowEnd component.
 */
const ChatWindowEnd = forwardRef<HTMLDivElement, ChatWindowEndProps>(
	(
		{ chatWindowEnd, setChatWindowEnd, handleEndConversation, setExpanded },
		ref
	): JSX.Element => {
		const config = useContext(ConfigContext);

		return (
			<div
				ref={ref}
				id='chat-end'
				className='wlcb-absolute wlcb-rounded-sm wlcb-z-50 wlcb-top-0 wlcb-left-0 wlcb-size-full wlcb-flex wlcb-items-center wlcb-justify-center wlcb-bg-black/50 wlcb-backdrop-blur-[1px] wlcb-px-space-250'
				role='dialog'
				aria-modal='true'
				tabIndex={-1}
			>
				<div className='wlcb-flex wlcb-flex-col wlcb-justify-between wlcb-bg-white wlcb-shadow-sm wlcb-rounded-md'>
					<div className='wlcb-p-space-150 wlcb-flex wlcb-flex-col wlcb-items-start wlcb-justify-start wlcb-gap-y-space-050 wlcb-select-none'>
						<h2
							className={cn('wlcb-text-heading-3 wlcb-text-text-primary', {
								'wlcb-text-theme-brand-secondary-text-color':
									config?.theme?.secondary !== undefined,
							})}
						>
							Vill du avsluta samtalet?
						</h2>
						<p
							className={cn('wlcb-text-caption wlcb-text-secondary', {
								'wlcb-text-theme-brand-secondary-text-color':
									config?.theme?.secondary !== undefined,
							})}
						>
							Välj avsluta för att avsluta konversationen.
						</p>
					</div>
					<div className='wlcb-flex wlcb-w-full'>
						<Button
							className='wlcb-flex-1 wlcb-rounded-bl-md !wlcb-justify-start'
							size='xl'
							variant='primary'
							aria-label='Avbryt'
							aria-description='Avbryter avslutningen av chatten och stänger avsluta modulen'
							aria-controls='chat-end'
							aria-expanded={chatWindowEnd}
							name='Avbryt'
							onClick={() => setChatWindowEnd(false)}
						/>
						<Button
							className='wlcb-flex-1 wlcb-rounded-br-md !wlcb-justify-start'
							aria-label='Avslutar Chat'
							aria-description='Rensar konversationen med ChatBot'
							aria-controls='chat-window-content'
							size='xl'
							variant='secondary'
							name='Avsluta chat'
							onClick={() => {
								handleEndConversation();
								setExpanded(false);
							}}
						/>
					</div>
				</div>
			</div>
		);
	}
);

ChatWindowEnd.displayName = 'ChatWindowEnd';
export default ChatWindowEnd;
